import { type RefObject, useCallback, useEffect, useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { Icon, Menu } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import styled from "styled-components"

import api, { asQuery } from "api"
import type { ItemRefs } from "components/ideas/useIdeaScrollTracking"
import { HoverActionsContentBox } from "components/lib/HoverActions"
import ReportContentChip from "components/lib/ReportContent/ReportContentChip"
import { TooltipButton } from "components/lib/UI"
import { useAppSelector } from "store"
import util from "utils/utils"

import DownloadCoverImageAction from "../Actions/DownloadCoverImageAction"
import DownloadPDFAction from "../Actions/DownloadPDFAction"
import FollowAction from "../Actions/FollowAction"
import JoinTeamAction from "../Actions/JoinTeamAction"
import LabelAction from "../Actions/LabelAction"
import LikeAction from "../Actions/LikeAction"
import PinAction from "../Actions/PinAction"
import ScrollToCommentsAction from "../Actions/ScrollToCommentsAction"
import TagAction from "../Actions/TagAction"
import ActivityManagement from "./ActivityManagement"
import { AssessorManagement } from "./AssessorManagement"
import CommsManagement from "./CommsManagement"
import { IdeaAssessment } from "./IdeaAssessment"
import ImpactManagement from "./ImpactManagement/ImpactManagement"
import ImpactStatementManagement from "./ImpactStatementManagement"
import { ProjectManagement } from "./ProjectManagement"

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
  height: 100%;

  .content-box {
    border-radius: 5px;
  }
`

const SubMenuItemContainer = styled.div`
  overflow: auto;
  padding-left: 5px;
  padding-right: 10px;
`

const ProjectTabs = {
  OVERVIEW: "overview",
  EMAILS: "emails",
  NOTES: "notes",
  ASSIGNEES: "assignees",
  ASSESSMENT: "assessment",
  ASSESSORS: "assessors",
  ACTIVITY: "activity",
  IMPACT: "impact",
}

type IdeaManagementProps = {
  idea: OpenAPI.GET<"/ideas/{id}">["response"]
  assessmentRoundId?: string
  assessment?: OpenAPI.GET<"/ideas/{id}">["response"]["assessments"][0]
  onUpdateIdea: (data: Partial<OpenAPI.PUT<"/ideas/{id}">["requestBody"]>) => void
  lanes?: { _id: string; name: string }[]
  updateProjectLane: (ideaId: string, laneId: string, position: number) => void
  onIdeaActivity: () => void
  assessmentEnabled?: boolean
  challenge?: OpenAPI.GET<"/challenges/{id}">["response"]
  sectionRefs?: RefObject<ItemRefs>
  fieldRefs?: RefObject<ItemRefs>
  scrollRef?: RefObject<HTMLDivElement>
}

const PreviewSideWindow = ({
  idea,
  onUpdateIdea,
  lanes = [],
  updateProjectLane,
  onIdeaActivity,
  assessmentEnabled,
  challenge: propChallenge,
  sectionRefs,
  fieldRefs,
  scrollRef,
}: IdeaManagementProps) => {
  const user = useAppSelector((state) => state.user)

  const ideaId = idea?._id
  const challengeId = idea?.challenge

  const board = useMemo(() => idea?.projectManagement?.boards?.find((b) => b.forId === idea.challenge), [idea])
  const userIsAssigned = useMemo(
    () => !!(board?.assignees || []).find((assigneeId) => assigneeId === user._id),
    [user, board],
  )
  const userIsAssessor = useMemo(
    () => !!(idea?.assessments || []).find((assessment) => assessment?.user === user._id),
    [idea, user],
  )

  const canManage = util.canManageChallenge(user, idea?.ownerChallenge)
  const canViewImpacts =
    canManage || userIsAssigned || util.hasPermission(user, "challenge.viewProjectImpacts", challengeId)

  const canViewProjectBoard =
    userIsAssigned || canManage || util.hasPermission(user, "challenge.viewProjectBoard", challengeId)

  const canViewProjectActivity = util.hasPermission(user, "challenge.viewProjectActivity", challengeId)
  const canViewAssessments = util.hasPermission(user, "challenge.viewAssessments", challengeId)
  const canViewSettings = util.hasPermission(user, "challenge.viewSettings", challengeId)

  const canViewTabMenu =
    canViewProjectBoard ||
    canManage ||
    userIsAssessor ||
    userIsAssigned ||
    canViewImpacts ||
    canViewProjectActivity ||
    canViewAssessments ||
    canViewSettings

  const [activeProjectTab, setActiveProjectTab] = useState(
    userIsAssessor ? ProjectTabs.ASSESSMENT : ProjectTabs.OVERVIEW,
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: When the idea id changes we want to reset the tab the user is viewing
  useEffect(() => {
    setActiveProjectTab(userIsAssessor ? ProjectTabs.ASSESSMENT : ProjectTabs.OVERVIEW)
  }, [ideaId, userIsAssessor])

  const [impactList, setImpactList] = useState(null)
  const { t } = useTranslation()
  const [currentLane, setCurrentLane] = useState("default")

  const usingChallenge = useMemo(() => propChallenge ?? idea?.ownerChallenge, [propChallenge, idea])

  useEffect(() => {
    const currentLaneId = board?.lane || "default"
    setCurrentLane(currentLaneId)
  }, [board])

  useEffect(() => {
    if (user.ownerOrganisation._id) {
      api.organisations.getImpacts(
        user.ownerOrganisation._id,
        ({ impacts }) => {
          setImpactList(impacts)
        },
        () => {},
      )
    }
  }, [user.ownerOrganisation._id])

  const handleUpdateProjectLane = useCallback(
    (laneId) => {
      updateProjectLane(ideaId, laneId, 0)
      setCurrentLane(laneId)
    },
    [ideaId, updateProjectLane],
  )

  const activityQuery = useQuery({
    queryKey: ["boards", idea?.challenge, "ideas", idea?._id, "events"],
    queryFn: asQuery(api.boards.getProjectIdeaEvents, {
      params: [idea?.challenge, idea?._id],
    }),
    enabled: userIsAssigned || canManage || util.hasPermission(user, "challenge.viewProjectActivity", challengeId),
  })
  const impactEnabled = user.ownerOrganisation.enabledFeatures.includes("ideaImpacts")
  const impactStatementsEnabled =
    user.ownerOrganisation.enabledFeatures.includes("impactStatements") && usingChallenge?.impactStatements?.enabled

  return (
    <ActionContainer data-testid="simplydo-project-board-actions">
      <HoverActionsContentBox $large $isOpen={canViewTabMenu}>
        <div className="content-box">
          <div
            style={{
              display: "flex",
              position: "relative",
              flexDirection: canViewTabMenu ? "row" : "column",
              gap: canViewTabMenu ? 0 : 4,
            }}
          >
            <LikeAction idea={idea} onUpdateIdea={onUpdateIdea} />
            <ScrollToCommentsAction idea={idea} />
            <FollowAction idea={idea} />
            <JoinTeamAction idea={idea} onUpdateIdea={onUpdateIdea} />

            <DownloadPDFAction idea={idea} />
            <DownloadCoverImageAction idea={idea} />

            <LabelAction idea={idea} onUpdateIdea={onUpdateIdea} />
            <TagAction idea={idea} onUpdateIdea={onUpdateIdea} />
            <PinAction idea={idea} onUpdateIdea={onUpdateIdea} />

            <div
              style={{
                width: canViewTabMenu ? 1 : 24,
                height: canViewTabMenu ? 24 : 1,
                alignSelf: "center",
                marginLeft: 2,
                marginRight: 5,
                background: "rgba(0, 0, 0, 0.1)",
              }}
            />

            <ReportContentChip
              forId={ideaId}
              forType="idea"
              trigger={
                <TooltipButton icon tooltip="Report">
                  <Icon name="flag" />
                </TooltipButton>
              }
            />
          </div>
          {canViewTabMenu ? (
            <>
              <Menu tabular size="tiny" stackable style={{ overflowX: "auto", overflowY: "hidden" }}>
                {canViewProjectBoard ? (
                  <Menu.Item
                    name="Overview"
                    active={activeProjectTab === ProjectTabs.OVERVIEW}
                    onClick={() => setActiveProjectTab(ProjectTabs.OVERVIEW)}
                  />
                ) : null}
                {userIsAssessor ? (
                  <Menu.Item
                    name="Assessment"
                    active={activeProjectTab === ProjectTabs.ASSESSMENT}
                    onClick={() => setActiveProjectTab(ProjectTabs.ASSESSMENT)}
                  />
                ) : null}
                {(userIsAssigned || canManage || canViewAssessments) && assessmentEnabled ? (
                  <Menu.Item
                    name="Assessors"
                    active={activeProjectTab === ProjectTabs.ASSESSORS}
                    onClick={() => setActiveProjectTab(ProjectTabs.ASSESSORS)}
                  />
                ) : null}
                {canManage || util.hasPermission(user, "challenge.viewSettings", challengeId) ? (
                  <Menu.Item
                    name="Emails"
                    active={activeProjectTab === ProjectTabs.EMAILS}
                    onClick={() => setActiveProjectTab(ProjectTabs.EMAILS)}
                  />
                ) : null}
                {(userIsAssigned || canManage || canViewProjectActivity) && activityQuery.data?.events?.length > 0 ? (
                  <Menu.Item
                    name="Activity"
                    active={activeProjectTab === ProjectTabs.ACTIVITY}
                    onClick={() => setActiveProjectTab(ProjectTabs.ACTIVITY)}
                  />
                ) : null}

                {impactEnabled && canViewImpacts && impactList?.length > 0 && (
                  <Menu.Item
                    name={`${t("common:capitalise", { key: "generic.impact" })}`}
                    active={activeProjectTab === ProjectTabs.IMPACT}
                    onClick={() => setActiveProjectTab(ProjectTabs.IMPACT)}
                  />
                )}
              </Menu>
              {canViewProjectBoard && activeProjectTab === ProjectTabs.OVERVIEW ? (
                <SubMenuItemContainer>
                  <ProjectManagement
                    idea={idea}
                    onUpdateIdea={onUpdateIdea}
                    onIdeaActivity={onIdeaActivity}
                    lanes={lanes}
                    currentLane={currentLane}
                    handleUpdateProjectLane={handleUpdateProjectLane}
                    canManage={canManage}
                  />
                </SubMenuItemContainer>
              ) : null}
              {userIsAssessor && activeProjectTab === ProjectTabs.ASSESSMENT ? (
                <SubMenuItemContainer>
                  <IdeaAssessment
                    idea={idea}
                    onUpdateIdea={onUpdateIdea}
                    sectionRefs={sectionRefs}
                    fieldRefs={fieldRefs}
                    scrollRef={scrollRef}
                  />
                </SubMenuItemContainer>
              ) : null}
              {(canManage || canViewSettings) && activeProjectTab === ProjectTabs.EMAILS ? (
                <SubMenuItemContainer>
                  <CommsManagement idea={idea} />
                </SubMenuItemContainer>
              ) : null}
              {(userIsAssigned || canManage || canViewAssessments) && activeProjectTab === ProjectTabs.ASSESSORS && (
                <SubMenuItemContainer>
                  <AssessorManagement idea={idea} challenge={usingChallenge} onUpdateIdea={onUpdateIdea} />
                </SubMenuItemContainer>
              )}
              {(userIsAssigned || canManage || canViewProjectActivity) &&
              activeProjectTab === ProjectTabs.ACTIVITY &&
              activityQuery.data?.events?.length > 0 ? (
                <SubMenuItemContainer>
                  <ActivityManagement idea={idea} activityQuery={activityQuery} />
                </SubMenuItemContainer>
              ) : null}
              {activeProjectTab === ProjectTabs.IMPACT &&
              impactEnabled &&
              !impactStatementsEnabled &&
              canViewImpacts &&
              impactList?.length > 0 ? (
                <SubMenuItemContainer>
                  <ImpactManagement
                    idea={idea}
                    onUpdateIdea={onUpdateIdea}
                    impactList={impactList}
                    canManage={userIsAssigned || canManage}
                  />
                </SubMenuItemContainer>
              ) : null}
              {activeProjectTab === ProjectTabs.IMPACT && impactEnabled && impactStatementsEnabled && canViewImpacts ? (
                <SubMenuItemContainer>
                  <ImpactStatementManagement idea={idea} onUpdateIdea={onUpdateIdea} impactList={impactList} />
                </SubMenuItemContainer>
              ) : null}
            </>
          ) : null}
        </div>
      </HoverActionsContentBox>
    </ActionContainer>
  )
}

export default PreviewSideWindow
